<template>
  <PVButton
    ref="buttonRef"
    class="standard-button"
    :data-testid="dataTestid"
    :label="resolvedLabel"
    :icon="resolvedIconOptions.icon ?? undefined"
    :disabled="isButtonBusyOrDisabled"
    :aria-busy="loading"
    :aria-label="buttonAriaLabel"
    :title="title"
    :type="type"
    :as="as"
    :class="[colorSchemeClass, classProp]"
    v-bind="{ ...routingProps, ...pVStyleProps }"
  >
    <span
      v-if="loading"
      class="loading-overlay"
      aria-hidden="true"
    >
      <StandardSpinner
        :size="size"
        :severity="severity"
        :variant="variant"
        :data-testid="`${dataTestid}-spinner`"
      />
    </span>
    <template v-if="resolvedIconOptions.isIconOnly">
      <slot name="icon">
        <Icon
          v-if="resolvedIconOptions.icon"
          :icon="resolvedIconOptions.icon"
          canvas-square
        />
      </slot>
    </template>
    <template v-else>
      <slot name="leftIcon">
        <Icon
          v-if="resolvedIconOptions.leftIcon"
          :icon="resolvedIconOptions.leftIcon"
          canvas-square
        />
      </slot>
      <slot v-if="shouldShowLabel">
        <!-- Supply default slot content so that {@link file://./StandardButtonWithFreeformDropdown.vue}
        can render the button label in the dropdown content when no default slot content is provided. -->
        {{ label }}
      </slot>
      <slot name="rightIcon">
        <Icon
          v-if="resolvedIconOptions.rightIcon"
          name="rightIcon"
          :icon="resolvedIconOptions.rightIcon"
          canvas-square
        />
      </slot>
    </template>
  </PVButton>
</template>
<script lang="ts">
export default {
  name: 'StandardButton',
  compatConfig: { MODE: 3 },
};
</script>
<script setup lang="ts">
import { ref, computed, useSlots } from 'vue';
import { ButtonSeverity, ButtonSize, ButtonVariant, type StandardButtonProps } from '@/types/StandardButtonTypes';
import {
  getButtonColorSchemeClass,
  getIconAccessibilityLabel,
  mapStandardButtonPropsToPVStyle,
  resolveStandardButtonIconOptions,
  type PrimeVueButtonStyledProps,
  type ResolvedStandardButtonIcons,
} from '@/utils/mapStandardButtonToPVStyle';
import { hasRenderedSlotContent } from '@/utils/vueSlots';

const props = withDefaults(defineProps<StandardButtonProps>(), {
  loading: false,
  disabled: false,
  fluid: false,
  rounded: false,
  label: '',
  // Platform default button size is small
  size: ButtonSize.SMALL,
  severity: ButtonSeverity.PRIMARY,
  variant: ButtonVariant.SOLID,
  type: 'button',
});

const slots = useSlots();

/**
 * Does NOT expose `href` prop because otherwise it will conflict with the `to` prop.
 *
 * If you need to use `href`, use a `b-button` instead.
 */
const buttonRef = ref<{ $el: HTMLElement } | null>(null);

const isButtonBusyOrDisabled = computed((): boolean => props.loading || props.disabled);

const pVStyleProps = computed((): PrimeVueButtonStyledProps => mapStandardButtonPropsToPVStyle(props));

const colorSchemeClass = computed((): string | null => getButtonColorSchemeClass(props.severity, props.variant));

const resolvedIconOptions = computed(
  (): ResolvedStandardButtonIcons =>
    resolveStandardButtonIconOptions({
      icon: props.icon,
      leftIcon: props.leftIcon,
      rightIcon: props.rightIcon,
    }),
);

const resolvedLabel = computed((): string | undefined => {
  if (resolvedIconOptions.value.isIconOnly) {
    return undefined;
  }

  return props.label;
});

const shouldShowLabel = computed((): boolean => {
  if (resolvedIconOptions.value.isIconOnly) {
    return false;
  }

  if (slots.default != null && hasRenderedSlotContent(slots.default())) {
    return true;
  }

  return props.label != null;
});

/**
 * Check if the component is rendering in icon-only mode.
 * Layout mode still comes from props (`resolvedIconOptions`); slots only override markup.
 */
const isIconOnlyButton = computed((): boolean => {
  if (resolvedIconOptions.value.isIconOnly) {
    return resolvedIconOptions.value.icon != null || slots.icon != null;
  }

  const hasDefaultSlotContent = slots.default != null && hasRenderedSlotContent(slots.default());

  return (
    !hasDefaultSlotContent &&
    !shouldShowLabel.value &&
    (resolvedIconOptions.value.leftIcon != null ||
      resolvedIconOptions.value.rightIcon != null ||
      slots.leftIcon != null ||
      slots.rightIcon != null)
  );
});

/**
 * Use label when it is passed; otherwise fall back to the icon name for iconOnly buttons.
 */
const buttonAriaLabel = computed((): string | undefined => {
  if (!isIconOnlyButton.value) {
    return undefined;
  }

  if (props.label !== '') {
    return props.label;
  }

  const icon =
    resolvedIconOptions.value.icon ?? resolvedIconOptions.value.leftIcon ?? resolvedIconOptions.value.rightIcon;

  if (icon == null) {
    return undefined;
  }

  return getIconAccessibilityLabel(icon);
});

/**
 * Only set `as` for non-button tags. Passing `as="button"` makes PrimeVue
 * ignore `unstyled` (https://github.com/primefaces/primevue/issues/8404).
 * Omitting it keeps the default `'BUTTON'`, which respects unstyled.
 */
const as = computed((): 'router-link' | 'a' | undefined => {
  if (isButtonBusyOrDisabled.value) return undefined;

  if (props.to) return 'router-link';

  if (props.href) return 'a';

  return undefined;
});

// Omit the `href` prop entirely if `to` is passed. Passing `href` as undefined (vs not passing it at all) to
// router-link will cause it to not generate a `href` property from the `to` prop.
const routingProps = computed(
  ():
    | { to: NonNullable<StandardButtonProps['to']>; target?: StandardButtonProps['target'] }
    | { href: string; target?: StandardButtonProps['target'] }
    | Record<string, never> => {
    if (props.to) {
      return {
        to: props.to,
        ...(props.target != null ? { target: props.target } : {}),
      };
    }

    if (props.href) {
      return {
        href: props.href,
        ...(props.target != null ? { target: props.target } : {}),
      };
    }

    return {};
  },
);

const focus = () => {
  buttonRef.value?.$el.focus();
};

defineExpose({
  focus,
});
</script>
<style scoped lang="scss">
// Severities whose outlined active state uses a label-color token (not background).
$standard-button-outlined-active-label-severities: 'secondary', 'info', 'success', 'warn', 'help', 'danger', 'contrast';

// custom color schemes, which supports variant = 'text '
$standard-button-text-color-schemes:
  'accent', 'item-under-analysis', 'variant', 'item-under-analysis-inverse', 'variant-inverse', 'primary-inverse',
  'accent-inverse', 'sentence-title', 'knowledge-base-inverse';

// custom color schemes, which supports variant = 'outlined'
$standard-button-outlined-color-schemes: 'accent', 'primary-inverse', 'accent-inverse';

// custom color schemes, which supports variant = 'solid'
$standard-button-solid-color-schemes: 'accent', 'primary-inverse', 'accent-inverse', 'iconsidebar';

//TODO: temporary overwrite icon box size in standard button. This has to be moved to ICON to standardize icon size later.
$standard-button-icon-size-small: 12px;
$standard-button-icon-size-normal: 14px;
$standard-button-icon-size-large: 18px;

@mixin standard-button-icon-square($size) {
  :deep(.fa-canvas-square) {
    box-sizing: content-box;
    width: $size;
    height: $size;
    padding: 0;
    margin: 0;
  }
}

@mixin standard-button-solid {
  @each $scheme in $standard-button-solid-color-schemes {
    &.standard-button--solid-#{$scheme} {
      // state: idle
      background: var(--p-component-color-schemes-button-#{$scheme}-background);
      color: var(--p-component-color-schemes-button-#{$scheme}-color);
      border-color: var(--p-component-color-schemes-button-#{$scheme}-border-color);

      // state: hover
      &:not(:disabled):hover {
        background: var(--p-component-color-schemes-button-#{$scheme}-hover-background);
        color: var(--p-component-color-schemes-button-#{$scheme}-hover-color);
        border-color: var(--p-component-color-schemes-button-#{$scheme}-hover-border-color);
      }

      // state: active
      &:not(:disabled):active {
        background: var(--p-component-color-schemes-button-#{$scheme}-active-background);
        color: var(--p-component-color-schemes-button-#{$scheme}-active-color);
        border-color: var(--p-component-color-schemes-button-#{$scheme}-active-border-color);
      }

      &:focus-visible {
        outline: var(--p-button-focus-ring-width)
          var(--p-button-focus-ring-style)
          var(--p-component-color-schemes-button-#{$scheme}-focus-ring-color);
        outline-offset: var(--p-button-focus-ring-offset);
      }
    }
  }
}

@mixin standard-button-outlined {
  &.p-button-outlined {
    &:not(:disabled):active {
      color: var(--p-component-color-schemes-button-outlined-primary-active-color);
    }

    @each $severity in $standard-button-outlined-active-label-severities {
      &.p-button-#{$severity}:not(:disabled):active {
        color: var(--p-component-color-schemes-button-outlined-#{$severity}-active-color);
      }
    }
  }

  @each $scheme in $standard-button-outlined-color-schemes {
    &.standard-button--outlined-#{$scheme}.p-button-outlined {
      // state: idle
      color: var(--p-component-color-schemes-button-outlined-#{$scheme}-color);
      border-color: var(--p-component-color-schemes-button-outlined-#{$scheme}-border-color);

      // state: hover
      &:not(:disabled):hover {
        background: var(--p-component-color-schemes-button-outlined-#{$scheme}-hover-background);
      }

      // state: active
      &:not(:disabled):active {
        background: var(--p-component-color-schemes-button-outlined-#{$scheme}-active-background);
        color: var(--p-component-color-schemes-button-outlined-#{$scheme}-active-color);
      }
    }
  }
}

@mixin standard-button-text {
  @each $scheme in $standard-button-text-color-schemes {
    &.standard-button--text-#{$scheme}.p-button-text {
      color: var(--p-component-color-schemes-button-text-#{$scheme}-color);

      &:not(:disabled):hover {
        background: var(--p-component-color-schemes-button-text-#{$scheme}-hover-background);
        color: var(--p-component-color-schemes-button-text-#{$scheme}-color);
      }

      &:not(:disabled):active {
        background: var(--p-component-color-schemes-button-text-#{$scheme}-active-background);

        // when variant or item-under-analysis, semantic contrast color is used (defined in design system)
        @if $scheme == 'variant' or $scheme == 'item-under-analysis' {
          color: var(--p-semantic-color-schemes-variant-contrast-color);
        } @else {
          color: var(--p-component-color-schemes-button-text-#{$scheme}-active-color);
        }
      }
    }
  }
}

.standard-button {
  // Anchor the loading/disabled overlay to the button box.
  position: relative;
  // When it is inside a flex container, it should not shrink.
  flex-shrink: 0;

  // Override Bootstrap body line-height (1.5) inherited by PrimeVue .p-button.
  line-height: normal;
  &,
  &:hover {
    text-decoration: none;
  }
  white-space: nowrap;

  // PrimeVue sets cursor: default on :disabled.
  &:disabled {
    cursor: not-allowed;
  }

  // PrimeVue `.p-button-sm` / `.p-button-lg` padding runs after `.p-button-icon-only`
  // and restores horizontal padding to --p-button-lg-padding-x;
  // set it to 0 for icon-only buttons to align with design system.
  &.p-button-lg.p-button-icon-only,
  &.p-button-sm.p-button-icon-only {
    padding-inline: 0;
  }

  &.p-button-sm {
    @include standard-button-icon-square($standard-button-icon-size-small);
  }

  &:not(.p-button-sm):not(.p-button-lg) {
    font-size: var(--p-semantic-commons-app-font-size);
    @include standard-button-icon-square($standard-button-icon-size-normal);
  }

  &.p-button-lg {
    @include standard-button-icon-square($standard-button-icon-size-large);
  }

  // style mapping based on button variant
  @include standard-button-outlined;
  @include standard-button-text;
  @include standard-button-solid;

  // The loading overlay only centers the spinner; it does not add a separate wash layer.
  .loading-overlay {
    position: absolute;
    inset: -1px; // -1px to account for the button border
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: inherit;
    z-index: 1;
  }
}
</style>
